import { NotificationModal } from '@components/ui/NotificationModal/NotificationModal';
import { useFilePreviewQuery } from '@dieterApi/file/useFilePreviewQuery';
import { CircularProgress } from '@mui/material';
import { MouseEvent, useCallback, useRef, useState } from 'react';

export interface SignaturePosition {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SignaturePositions {
  signature1: SignaturePosition;
  signature2: SignaturePosition;
}

interface Props {
  open: boolean;
  fileId: string;
  token?: string;
  onPositionsSelected: (positions: SignaturePositions) => void;
  onCancel: () => void;
}

type PlacingMode = 'signature1' | 'signature2' | 'done';

const MARKER_WIDTH_PCT = 30; // marker width as % of page width
const MARKER_HEIGHT_PCT = 10; // marker height as % of page height

// Output dimensions in PDF points (double the original 89×42pt)
const SIGNATURE_WIDTH_PT = 180;
const SIGNATURE_HEIGHT_PT = 85;

export function SignaturePositionSelector({ open, fileId, token, onPositionsSelected, onCancel }: Props) {
  const { data, loading, error } = useFilePreviewQuery({ id: fileId, token, skip: !open });
  const [placingMode, setPlacingMode] = useState<PlacingMode>('signature1');
  const [marker1, setMarker1] = useState<{ page: number; xPct: number; yPct: number } | null>(null);
  const [marker2, setMarker2] = useState<{ page: number; xPct: number; yPct: number } | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handlePageClick = useCallback(
    (e: MouseEvent<HTMLDivElement>, pageIndex: number) => {
      const container = pageRefs.current[pageIndex];
      if (!container || placingMode === 'done') return;

      const rect = container.getBoundingClientRect();
      const xPct = ((e.clientX - rect.left) / rect.width) * 100;
      const yPct = ((e.clientY - rect.top) / rect.height) * 100;

      // Clamp so marker stays within page bounds
      const clampedX = Math.max(0, Math.min(xPct, 100 - MARKER_WIDTH_PCT));
      const clampedY = Math.max(0, Math.min(yPct, 100 - MARKER_HEIGHT_PCT));

      if (placingMode === 'signature1') {
        setMarker1({ page: pageIndex, xPct: clampedX, yPct: clampedY });
        setPlacingMode('signature2');
      } else if (placingMode === 'signature2') {
        setMarker2({ page: pageIndex, xPct: clampedX, yPct: clampedY });
        setPlacingMode('done');
      }
    },
    [placingMode]
  );

  const handleReset = () => {
    setMarker1(null);
    setMarker2(null);
    setPlacingMode('signature1');
  };

  const handleClose = () => {
    handleReset();
    onCancel();
  };

  const handleConfirm = () => {
    if (!marker1 || !marker2) return;

    // Convert percentage-based positions to positions for 595x842 PDF points (A4)
    // The backend will handle the actual PDF page size; we use standard A4 dimensions as reference
    const PDF_WIDTH = 595;
    const PDF_HEIGHT = 842;

    const positions: SignaturePositions = {
      signature1: {
        page: marker1.page + 1,
        x: Math.round((marker1.xPct / 100) * PDF_WIDTH),
        y: Math.round((marker1.yPct / 100) * PDF_HEIGHT),
        width: SIGNATURE_WIDTH_PT,
        height: SIGNATURE_HEIGHT_PT,
      },
      signature2: {
        page: marker2.page + 1,
        x: Math.round((marker2.xPct / 100) * PDF_WIDTH),
        y: Math.round((marker2.yPct / 100) * PDF_HEIGHT),
        width: SIGNATURE_WIDTH_PT,
        height: SIGNATURE_HEIGHT_PT,
      },
    };

    handleReset();
    onPositionsSelected(positions);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-12">
          <CircularProgress size={32} />
          <span className="ml-3 text-gray-500">Dokument wird geladen...</span>
        </div>
      );
    }

    if (error || !data?.getFilePreview?.length) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          Fehler beim Laden der Dokumentvorschau.
        </div>
      );
    }

    const pages = data.getFilePreview;

    return (
      <>
        {/* PDF Pages */}
        <div className="overflow-y-auto border rounded bg-gray-100 p-2" style={{ maxHeight: 'calc(85vh - 180px)' }}>
          {pages.map((pageImg, idx) => (
            <div
              key={idx}
              ref={(el) => (pageRefs.current[idx] = el)}
              className="relative mb-2 cursor-crosshair select-none"
              onClick={(e) => handlePageClick(e, idx)}
              style={{ position: 'relative' }}
            >
              <img src={pageImg} alt={`Seite ${idx + 1}`} className="w-full pointer-events-none" draggable={false} />
              {/* Page number label */}
              <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                Seite {idx + 1}
              </div>

              {/* Marker 1 */}
              {marker1 && marker1.page === idx && (
                <div
                  className="absolute border-2 border-blue-600 bg-blue-500/20 rounded pointer-events-none flex items-center justify-center"
                  style={{
                    left: `${marker1.xPct}%`,
                    top: `${marker1.yPct}%`,
                    width: `${MARKER_WIDTH_PCT}%`,
                    height: `${MARKER_HEIGHT_PCT}%`,
                  }}
                >
                  <span className="text-blue-700 font-bold text-xs bg-white/80 px-1 rounded">Unterschrift 1</span>
                </div>
              )}

              {/* Marker 2 */}
              {marker2 && marker2.page === idx && (
                <div
                  className="absolute border-2 border-orange-600 bg-orange-500/20 rounded pointer-events-none flex items-center justify-center"
                  style={{
                    left: `${marker2.xPct}%`,
                    top: `${marker2.yPct}%`,
                    width: `${MARKER_WIDTH_PCT}%`,
                    height: `${MARKER_HEIGHT_PCT}%`,
                  }}
                >
                  <span className="text-orange-700 font-bold text-xs bg-white/80 px-1 rounded">Unterschrift 2</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end mt-3">
          <button onClick={handleClose} className="text-sm underline text-gray-500 hover:text-gray-700">
            Abbrechen
          </button>
          <button
            onClick={handleReset}
            className="text-sm underline text-primary-root disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!marker1 && !marker2}
          >
            Zurücksetzen
          </button>
          <button
            onClick={handleConfirm}
            className="text-sm bg-primary-root text-white px-4 py-1.5 rounded hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={placingMode !== 'done'}
          >
            Positionen bestätigen
          </button>
        </div>
      </>
    );
  };

  return (
    <NotificationModal open={open} onClose={handleClose} maxWidth="900px">
      <div className="flex flex-col gap-3 p-2">
        <h2 className="text-lg font-semibold">Unterschriftspositionen festlegen</h2>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
          {placingMode === 'signature1' && (
            <span>
              <strong>Schritt 1:</strong> Klicke auf die Stelle, an der die{' '}
              <span className="text-blue-700 font-semibold">erste Unterschrift</span> platziert werden soll.
            </span>
          )}
          {placingMode === 'signature2' && (
            <span>
              <strong>Schritt 2:</strong> Klicke auf die Stelle, an der die{' '}
              <span className="text-orange-700 font-semibold">zweite Unterschrift</span> platziert werden soll.
            </span>
          )}
          {placingMode === 'done' && (
            <span>
              <strong>Fertig!</strong> Beide Positionen wurden festgelegt. Du kannst die Positionen zurücksetzen oder
              bestätigen.
            </span>
          )}
        </div>

        {renderContent()}
      </div>
    </NotificationModal>
  );
}
